/**
 * Presentation ViewModels — UI-specific formatting for PLP
 */

import type {
  ListingMeta,
  ListingPageResult,
  ListingFaqItem,
  ListingDesignItem,
  ListingFilter,
} from '../../domain/models/ProductListing';

export type { FiltersPreviewPresentationVm } from '@/features/filters/presentation/viewModels/filters.vm';
export { toFiltersPreviewPresentationVm } from '@/features/filters/presentation/viewModels/filters.vm';

export type { ExploreLinkPresentationVm, ListingSeoPresentationVm } from '@/shared/features/seo/presentation/viewModels/seo.vm';
export { toSeoPresentationVm, toExploreLinksPresentation } from '@/shared/features/seo/presentation/viewModels/seo.vm';
import { toSeoPresentationVm, toExploreLinksPresentation } from '@/shared/features/seo/presentation/viewModels/seo.vm';
import type { ExploreLinkPresentationVm, ListingSeoPresentationVm } from '@/shared/features/seo/presentation/viewModels/seo.vm';

import {
  formatPlpCategoryLabel,
  getJewelleryBrowseBreadcrumbs,
  type PlpBreadcrumbItem,
} from '../../utils/plpBreadcrumb';
import { getPlpTagFromUrl } from '../../utils/plpUrl';

export interface ListingMetaPresentationVm {
  headerLabel: string;
  header: string;
  headerImageUrl?: string;
  totalCount: number;
  filtersResetUrl?: string;
  exploreLinks?: ExploreLinkPresentationVm[];
  listingSeo?: ListingSeoPresentationVm;
  faqs?: ListingFaqItem[];
  filters: ListingFilter[];
}

export interface ListingPagePresentationVm {
  meta: ListingMetaPresentationVm | null;
  designs: ListingDesignItem[];
  redirectUrl?: string;
}

export interface PlpPresentationContext {
  pathname: string;
  isStorePlp: boolean;
  isSearchPlp: boolean;
  storeCitySlug?: string;
  cityDisplayName?: string;
  storeAreaName?: string;
}

export function toListingMetaPresentationVm(
  meta: ListingMeta | null,
  ctx: PlpPresentationContext
): ListingMetaPresentationVm | null {
  if (!meta) return null;
  const headerLabel =
    meta.header?.trim() ||
    formatPlpCategoryLabel(getPlpTagFromUrl(ctx.pathname));
  const listingSeo = toSeoPresentationVm(meta.listingSeo);
  const exploreLinks =
    toExploreLinksPresentation(meta.exploreLinks) ??
    listingSeo?.seoLinks;
  return {
    headerLabel,
    header: meta.header,
    ...(meta.headerImageUrl && { headerImageUrl: meta.headerImageUrl }),
    totalCount: meta.totalCount,
    filters: meta.filters,
    ...(meta.filtersResetUrl && { filtersResetUrl: meta.filtersResetUrl }),
    ...(exploreLinks?.length && { exploreLinks }),
    ...(listingSeo && { listingSeo }),
    ...(meta.faqs?.length && { faqs: meta.faqs }),
  };
}

export function toListingPagePresentationVm(
  result: ListingPageResult | null,
  ctx: PlpPresentationContext
): ListingPagePresentationVm {
  if (!result) {
    return { meta: null, designs: [] };
  }
  const meta: ListingMeta = {
    header: result.header,
    ...(result.headerImageUrl && { headerImageUrl: result.headerImageUrl }),
    totalCount: result.totalCount,
    filters: result.filters,
    ...(result.filtersResetUrl && { filtersResetUrl: result.filtersResetUrl }),
    ...(result.exploreLinks?.length && { exploreLinks: result.exploreLinks }),
    ...(result.listingSeo && { listingSeo: result.listingSeo }),
    ...(result.faqs?.length && { faqs: result.faqs }),
  };
  return {
    meta: toListingMetaPresentationVm(meta, ctx),
    designs: result.designs,
    ...(result.redirectUrl && { redirectUrl: result.redirectUrl }),
  };
}

export function toPlpBreadcrumbPresentationVm(ctx: PlpPresentationContext & {
  headerLabel?: string;
}): PlpBreadcrumbItem[] | undefined {
  if (ctx.isSearchPlp) {
    return [{ label: 'Home', to: '/' }, { label: 'Search' }];
  }
  if (ctx.isStorePlp) {
    const areaName = ctx.storeAreaName;
    const cityDisplayName = ctx.cityDisplayName ?? '';
    const items: PlpBreadcrumbItem[] = [
      { label: 'Home', to: '/' },
      { label: 'Jewellery Stores', to: '/store.html', shouldRefresh: true },
    ];
    if (cityDisplayName) {
      const cityPath =
        ctx.storeCitySlug ||
        cityDisplayName
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .toLowerCase();
      if (areaName) {
        items.push({
          label: `BlueStone Jewellery Shops in ${cityDisplayName}`,
          to: cityPath ? `/stores/${cityPath}/catalog.html` : undefined,
          shouldRefresh: true,
        });
        items.push({ label: `BlueStone Jewellery Shop in ${areaName}` });
      } else {
        items.push({ label: `BlueStone Jewellery Shops in ${cityDisplayName}` });
      }
    }
    return items;
  }
  const fallback = ctx.headerLabel ?? formatPlpCategoryLabel(getPlpTagFromUrl(ctx.pathname));
  return getJewelleryBrowseBreadcrumbs(ctx.pathname, fallback);
}

export function toPlpStoreCountLabelPresentationVm(ctx: {
  isStorePlp: boolean;
  storeAreaName?: string;
  cityDisplayName?: string;
}): string {
  if (!ctx.isStorePlp) return 'Designs';
  const storeName = ctx.storeAreaName;
  const cityDisplayName = ctx.cityDisplayName ?? '';
  if (storeName && cityDisplayName) {
    return `Jewellery Designs in BlueStone ${storeName}, ${cityDisplayName} store`;
  }
  if (storeName) return `Jewellery Designs in BlueStone ${storeName} store`;
  if (cityDisplayName) return `Jewellery Designs in ${cityDisplayName}`;
  return 'Designs';
}
